import type { DjinnLedger, DjinnLedgerRow } from "@magicweave/api";

/** What the lamp and the rail's row say: six states, one of them at a time. */
export type LampMood = "calm" | "found" | "several" | "working" | "waiting" | "down";

export interface LampRead {
  mood: LampMood;
  /** The words beside the lamp; null when there are none (calm). */
  say: string | null;
  /** What the watch raised that nobody has acted on, newest first. */
  findings: DjinnLedgerRow[];
  /** Fixes and check-back results waiting on this person. Empty for someone who cannot act. */
  forYou: DjinnLedgerRow[];
  /** The fix that runs, when one does. */
  running: DjinnLedgerRow | null;
  /** Seconds the running fix had run when the ledger was read. */
  elapsedS: number;
  /** The watch is off or Djinn cannot start work: the rail says "paused". */
  paused: boolean;
}

/** A row the watch raised: no one started it, and it is not a check-back's outcome. */
/** A finding's title with room to spare: "Gold is piling up" is "Gold piling up" beside a "+1 more". */
export const shortTitle = (title: string): string => title.replace(/\s(?:is|are)\s/, " ");

const raisedByWatch = (r: DjinnLedgerRow) => r.by === undefined && !r.outcome && !r.label;

/** The newest first, by when each was proposed. */
const newest = (a: DjinnLedgerRow, b: DjinnLedgerRow) =>
  Date.parse(b.proposed_at ?? "") - Date.parse(a.proposed_at ?? "") || 0;

/**
 * What the ledger says Djinn is doing, in the order a person needs it: it cannot think, a fix runs
 * (for everyone in the game), the watch found something, something waits on you, a check-back is
 * due, or nothing. A viewer has nothing "waiting on you" because they cannot act on it; the
 * ledger's own `can_pause` is the right to act. Words only come from the read, never a count of ours.
 */
export function lampRead(ledger: DjinnLedger | undefined, nowMs: number): LampRead {
  const none: LampRead = {
    mood: "calm",
    say: null,
    findings: [],
    forYou: [],
    running: null,
    elapsedS: 0,
    paused: false,
  };
  if (!ledger) return none;
  const mayAct = ledger.watch.can_pause;
  const findings = ledger.waiting.filter(raisedByWatch).sort(newest);
  const forYou = mayAct ? ledger.waiting.filter((r) => !raisedByWatch(r)) : [];
  const running = ledger.running[0] ?? null;
  const due = ledger.done.some(
    (r) => r.outcome === "due" && r.at != null && Date.parse(r.at) <= nowMs,
  );
  const paused =
    ledger.watch.paused ||
    ledger.cannot_run === "staff_paused" ||
    ledger.cannot_run === "credits_out";
  const read: LampRead = {
    ...none,
    findings,
    forYou,
    running,
    elapsedS: running?.elapsed_s ?? 0,
    paused,
  };
  if (ledger.cannot_run === "cant_think")
    return { ...read, mood: "down", say: "can’t think right now" };
  if (running) return { ...read, mood: "working", say: "Testing a fix" };
  const first = findings[0];
  if (first)
    return findings.length > 1
      ? {
          ...read,
          mood: "several",
          say: `${shortTitle(first.title)} · +${String(findings.length - 1)} more`,
        }
      : { ...read, mood: "found", say: first.title };
  if (forYou.length > 0)
    return { ...read, mood: "waiting", say: `${String(forYou.length)} waiting on you` };
  if (due) return { ...read, mood: "found", say: "A check-back is ready" };
  // Paused only says so when nothing else is going on: it never hides a fix or a card waiting on you.
  if (ledger.cannot_run === "credits_out")
    return { ...read, mood: "down", say: "is out of credits" };
  if (ledger.cannot_run === "staff_paused") return { ...read, mood: "down", say: "is paused" };
  return read;
}
